"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { formatDuration } from "@huub-web/utils";

export type UseCountdownOptions = {
  /**
   * Total time budget for the countdown, in seconds.
   */
  limitSeconds: number;
  /**
   * Remaining seconds at or below which `shouldWarn` becomes true.
   */
  warnAtSeconds: number;
  /**
   * Fired exactly once per lifetime, when the countdown reaches 0.
   */
  onExpire: () => void;
};

/**
 * Total-elapsed countdown. Recomputes remaining time from wall clock each
 * tick so tab-throttling doesn't desync the display. Fires `onExpire`
 * exactly once when remaining reaches 0.
 */
export const useCountdown = ({
  limitSeconds,
  warnAtSeconds,
  onExpire,
}: UseCountdownOptions) => {
  const startedAtRef = useRef<number>(Date.now());
  const [remainingSeconds, setRemainingSeconds] =
    useState<number>(limitSeconds);
  const hasExpiredRef = useRef<boolean>(false);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  useEffect(() => {
    const tick = () => {
      const elapsed = Math.floor((Date.now() - startedAtRef.current) / 1000);
      const remaining = Math.max(0, limitSeconds - elapsed);
      setRemainingSeconds(remaining);
      if (remaining === 0 && !hasExpiredRef.current) {
        hasExpiredRef.current = true;
        onExpireRef.current();
      }
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [limitSeconds]);

  const reset = useCallback(() => {
    startedAtRef.current = Date.now();
    hasExpiredRef.current = false;
    setRemainingSeconds(limitSeconds);
  }, [limitSeconds]);

  return {
    remainingSeconds,
    formatted: formatDuration(remainingSeconds, { timeFormat: true }),
    shouldWarn: remainingSeconds <= warnAtSeconds && remainingSeconds > 0,
    reset,
  };
};
